import { defineStore } from 'pinia';
import { shouldThrottle } from '@/util/rateLimit.js';
import apiClient from '@/util/apiClient';
import posthog from '@/composables/usePostHog.js';
import EGS from '@/util/egs_util.ts';

type IdReference<T extends string> = number | { [K in T]?: number } | null | undefined;

export type MatchReportStatus = string;

export function getMatchReportStatuses(): string[] {
	return Array.isArray(EGS.Enums.matchReportStatus) ? EGS.Enums.matchReportStatus : [];
}
function asMatchReportDTO(value: unknown): MatchReportDTO | null {
	if (!value || typeof value !== 'object') {
		return null;
	}

	return value as MatchReportDTO;
}
function asMatchReportDTOArray(value: unknown): MatchReportDTO[] {
	if (!Array.isArray(value)) {
		return [];
	}

	return value as MatchReportDTO[];
}
export interface MatchReportDTO {
	reportNumber?: number | null;
	createdOn?: string | null;
	updatedOn?: string | null;

	matchNumber?: IdReference<'matchNumber'>;
	groupNumber?: IdReference<'groupNumber'>;
	userNumber?: IdReference<'userNumber'>;

	position?: string | null;
	officialPosition?: string | null;

	homeTeamScore?: number | string | null;
	awayTeamScore?: number | string | null;

	homeTeamCautions?: number | string | null;
	homeTeamSendOffs?: number | string | null;
	awayTeamCautions?: number | string | null;
	awayTeamSendOffs?: number | string | null;

	// Legacy/calculated display fields if your backend returns these.
	cautions?: number | string | null;
	sendOffs?: number | string | null;

	notes?: string | null;
	status?: MatchReportStatus | null;
	/** Finance V3 review state; the source of truth for pay status when present. */
	reviewStatus?: string | null;

	fee?: number | null;
	travelFee?: number | null;
	groupFee?: number | null;
	assignorFee?: number | null;
	hours?: number | null;
	payPeriod?: number | string | null;
	paid?: number | string | null;

	[key: string]: unknown;
}
export interface SaveMatchReportPayload {
	homeTeamScore: number | string;
	awayTeamScore: number | string;
	homeTeamCautions: number | string;
	homeTeamSendOffs: number | string;
	awayTeamCautions: number | string;
	awayTeamSendOffs: number | string;
	groupNumber: number;
	matchNumber: number;
	userNumber: number;
	position: string;
	status: MatchReportStatus;
	notes?: string | null;
}
export type MatchReportPatchPayload = Partial<MatchReportDTO>;
interface MatchReportState {
	matchReports: MatchReportDTO[];
	myMatchReports: MatchReportDTO[];
	selectedMatchReports: MatchReportDTO[];
}
function normalizeIdReference<T extends string>(value: IdReference<T>, key: T): number | null | undefined {
	if (value == null) return value;
	if (typeof value === 'object') {
		const nestedValue = value[key];
		return typeof nestedValue === 'number' ? nestedValue : null;
	}
	return value;
}

function normalizeMatchReportPatchPayload(matchReport: MatchReportPatchPayload): MatchReportPatchPayload {
	if (typeof matchReport !== 'object') return matchReport;
	return {
		...matchReport,
		matchNumber: normalizeIdReference(matchReport.matchNumber, 'matchNumber'),
		groupNumber: normalizeIdReference(matchReport.groupNumber, 'groupNumber'),
		userNumber: normalizeIdReference(matchReport.userNumber, 'userNumber')
	};
}
function normalizeMyReport(report: MatchReportDTO): MatchReportDTO {
	if (typeof report.matchNumber === 'number') {
		return {
			...report,
			matchNumber: { matchNumber: report.matchNumber }
		};
	}
	return report;
}

export const useMatchReportStore = defineStore('matchReportStore', {
	state: (): MatchReportState => ({
		matchReports: [],
		myMatchReports: [],
		selectedMatchReports: []
	}),
	getters: {
		getReportsByMatchNumber:
			(state) =>
			(matchNumber: number): MatchReportDTO[] => {
				return state.matchReports.filter((report) => {
					const reportMatchNumber = normalizeIdReference(report.matchNumber as IdReference<'matchNumber'>, 'matchNumber');
					return reportMatchNumber === matchNumber;
				});
			},
		getMyReportByMatchNumber:
			(state) =>
			(matchNumber: number): MatchReportDTO | null => {
				return (
					state.myMatchReports.find((report) => {
						const reportMatchNumber = normalizeIdReference(report.matchNumber as IdReference<'matchNumber'>, 'matchNumber');
						return reportMatchNumber === matchNumber;
					}) ?? null
				);
			}
	},
	actions: {
		async fetchAndProcessMatchReports(url: string, data: unknown = null): Promise<MatchReportDTO[]> {
			try {
				const response = data ? await apiClient.post<MatchReportDTO[]>(url, data) : await apiClient.post<MatchReportDTO[]>(url);

				return Array.isArray(response.data) ? response.data : [];
			} catch (error) {
				posthog.capture('match_report_fetch_failed', { url, data, error });
				return [];
			}
		},
		async fetchMyMatchReports(): Promise<MatchReportDTO[]> {
			if (shouldThrottle('fetchMyMatchReports', 100)) {
				posthog.capture('throttled_action', { action: 'fetchMyMatchReports' });
				return this.myMatchReports;
			}
			try {
				const response = await apiClient.get('/match/report/my-reports');
				const reports = Array.isArray(response.data) ? response.data : [];

				this.myMatchReports = reports.map(normalizeMyReport);
				return this.myMatchReports;
			} catch {
				this.myMatchReports = [];
				return [];
			}
		},

		async fetchClientMatchReports(clientNumbers: number[]): Promise<MatchReportDTO[]> {
			if (shouldThrottle('fetchClientMatchReports', 100)) {
				posthog.capture('throttled_action', { action: 'fetchClientMatchReports' });
				return this.matchReports;
			}
			try {
				const reports = await this.fetchAndProcessMatchReports('/match/report/clients', clientNumbers);
				this.matchReports = [...reports];
				return reports;
			} catch {
				this.matchReports = [];
				return [];
			}
		},

		async fetchGroupMatchReports(groupNumbers: number[]): Promise<MatchReportDTO[]> {
			if (shouldThrottle('fetchGroupMatchReports', 100)) {
				posthog.capture('throttled_action', { action: 'fetchGroupMatchReports' });
				return this.matchReports;
			}
			try {
				const reports = await this.fetchAndProcessMatchReports('/match/report/groups', groupNumbers);
				this.matchReports = [...reports];
				return reports;
			} catch {
				this.matchReports = [];
				return [];
			}
		},

		async fetchMatchReportByNumber(reportNumber: number): Promise<MatchReportDTO | null> {
			if (shouldThrottle(`fetchMatchReport:${reportNumber}`, 100)) {
				posthog.capture('throttled_action', { action: 'fetchMatchReport', reportNumber });
				return null;
			}

			const response = await apiClient.get<unknown>(`/match/report/number/${reportNumber}`);
			return asMatchReportDTO(response.data);
		},

		async fetchMatchReportsByMatchNumber(matchNumber: number): Promise<MatchReportDTO[]> {
			if (shouldThrottle(`fetchMatchReportsByMatch:${matchNumber}`, 100)) {
				posthog.capture('throttled_action', {
					action: 'fetchMatchReportsByMatchNumber',
					matchNumber
				});
				return [];
			}

			const response = await apiClient.get<unknown>(`/match/report/match/${matchNumber}`);
			const reports = asMatchReportDTOArray(response.data);

			this.selectedMatchReports = reports;
			return reports;
		},

		async saveMatchReport(matchReport: SaveMatchReportPayload): Promise<MatchReportDTO | null> {
			if (shouldThrottle('saveMatchReport', 100)) {
				posthog.capture('throttled_action', { action: 'saveMatchReport' });
				return null;
			}

			const response = await apiClient.post<unknown>('/match/report/save', matchReport);
			return asMatchReportDTO(response.data);
		},

		async updateUsersMatchReport(reportNumber: number, matchReport: MatchReportPatchPayload): Promise<MatchReportDTO | null> {
			if (shouldThrottle(`updateMatchReport:${reportNumber}`, 100)) {
				posthog.capture('throttled_action', { action: 'updateMatchReport', reportNumber });
				return null;
			}

			const payload = normalizeMatchReportPatchPayload(matchReport);
			const response = await apiClient.patch<unknown>(`/match/report/edit/${reportNumber}`, payload);

			return asMatchReportDTO(response.data);
		}
	}
});
